import React from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";

export const Eyebrow = ({ children, className = "" }) => (
  <div className={`flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.12em] text-blue-700 ${className}`}>
    <span className="h-1.5 w-1.5 rounded-full bg-blue-600 inline-block" />
    {children}
  </div>
);

export const SectionTitle = ({ children, className = "" }) => (
  <h2 className={`mt-4 text-3xl sm:text-4xl md:text-[2.75rem] font-bold tracking-tight text-slate-900 leading-[1.1] ${className}`}>
    {children}
  </h2>
);

export const BulletList = ({ items }) => (
  <ul className="mt-6 space-y-3">
    {items.map((item) => (
      <li key={item} className="flex items-start gap-3 text-slate-700">
        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-50">
          <Check className="h-3 w-3 text-blue-700" strokeWidth={3} />
        </span>
        <span className="text-[15px] leading-relaxed">{item}</span>
      </li>
    ))}
  </ul>
);

export const PrimaryButton = ({ to = "/book-demo", children, className = "", ...props }) => (
  <Link
    to={to}
    data-testid={`primary-link-${to.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '')}`}
    {...props}
    className={`inline-flex items-center justify-center rounded-lg bg-blue-700 px-6 py-3 text-[15px] font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-blue-800 ${className}`}
  >
    {children}
  </Link>
);

export const SecondaryButton = ({ to, children, className = "", ...props }) => (
  <Link
    to={to}
    data-testid={`secondary-link-${to.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '')}`}
    {...props}
    className={`inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-6 py-3 text-[15px] font-semibold text-slate-800 transition-colors duration-200 hover:border-slate-300 hover:bg-slate-50 ${className}`}
  >
    {children}
  </Link>
);

export const PageHeader = ({ eyebrow, title, subtitle }) => (
  <div className="site-container retained-page-header">
    <div className="eyebrow">{eyebrow}</div>
    <h1 data-testid="page-heading">{title}</h1>
    {subtitle && <p data-testid="page-subtitle">{subtitle}</p>}
  </div>
);

// Two-column feature section used across Product page
export const FeatureSection = ({ eyebrow, title, body, bullets, visual, reversed = false, bg = "white" }) => (
  <section className={`${bg === "gray" ? "bg-slate-50" : "bg-white"} py-20 md:py-24`}>
    <div className="mx-auto max-w-7xl px-6">
      <div className={`grid items-center gap-12 lg:grid-cols-2 lg:gap-20 ${reversed ? "lg:[&>*:first-child]:order-2" : ""}`}>
        <div>
          <Eyebrow>{eyebrow}</Eyebrow>
          <h3 className="mt-4 text-2xl sm:text-3xl md:text-[2rem] font-bold tracking-tight text-slate-900 leading-tight">{title}</h3>
          <p className="mt-4 text-[16px] text-slate-600 leading-relaxed">{body}</p>
          {bullets && <BulletList items={bullets} />}
        </div>
        <div>{visual}</div>
      </div>
    </div>
  </section>
);
